import { useMemo } from "react";
import { Flex, Text, useMantineTheme } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import { enumLabel, GOAL_CATEGORY_LABEL, INVESTMENT_TYPE_LABEL } from "@/shared/constants/general.const";
import { formatINR, formatDate } from "@/shared/utils/format.utils";

type GoalRow = {
  id: string;
  createdAt: string;
  category: string;
  goalName: string;
  goalAmount: number | null;
  goalAchievedPct: number | null;
  totalInvestments: number;
  actualInvested: number;
  currentValue: number | null;
  growthPct: number | null;
  schemeName: string | null;
  investmentType: string;
  tenureYears: number;
};

type Props = {
  goals: any[];
  orders: any[];
  sxps: any[];
};

const dash = <span style={{ color: "var(--mantine-color-slate-4)" }}>—</span>;

function PctCell({ value }: { value: number | null }) {
  if (value == null) return dash;
  const color = value >= 0 ? "var(--mantine-color-green-7)" : "var(--mantine-color-red-6)";
  return <span style={{ color }}>{value.toFixed(2)}%</span>;
}

export default function GoalsTab({ goals, orders, sxps }: Props) {
  const theme = useMantineTheme();

  // Build goalId → scheme name from linked orders/sxps
  const schemeByGoalId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const o of orders) {
      if (o.goalId && o.scheme) map[o.goalId] = o?.masterScheme?.name ?? o.scheme ?? "—";
    }
    for (const s of sxps) {
      if (s.goalId && s.srcScheme && !map[s.goalId]) map[s.goalId] = s?.masterScheme?.name ?? s.srcScheme ?? "—";
    }
    return map;
  }, [orders, sxps]);

  // Metrics (actualInvested, currentValue, growthPct, goalAchievedPct) are pre-computed by the API
  const rows: GoalRow[] = useMemo(
    () =>
      goals.map((g) => ({
        id: g.id,
        createdAt: g.createdAt,
        category: g.category,
        goalName: g.goalName,
        goalAmount: g.goalAmount ?? null,
        goalAchievedPct: g.goalAchievedPct ?? null,
        totalInvestments: g.totalInvestments,
        actualInvested: g.actualInvested ?? 0,
        currentValue: g.currentValue ?? null,
        growthPct: g.growthPct ?? null,
        schemeName: schemeByGoalId[g.id] ?? null,
        investmentType: g.investmentType,
        tenureYears: g.tenureYears,
      })),
    [goals, schemeByGoalId]
  );

  const columns: MRT_ColumnDef<GoalRow>[] = useMemo(
    () => [
      {
        accessorKey: "createdAt",
        header: "Goal Created On",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
      {
        accessorKey: "category",
        header: "Goal Category",
        size: 160,
        Cell: ({ cell }) => enumLabel(GOAL_CATEGORY_LABEL, cell.getValue<string>()) ?? "—",
      },
      {
        accessorKey: "goalName",
        header: "Goal Name",
        size: 180,
      },
      {
        accessorKey: "tenureYears",
        header: "Tenure",
        size: 100,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const val = cell.getValue<number>();
          return val != null ? `${val} years` : "—";
        },
      },
      {
        accessorKey: "goalAmount",
        header: "Goal Amount",
        size: 140,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | null>()),
      },
      {
        accessorKey: "totalInvestments",
        header: "Total Invested",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number>()),
      },
      {
        accessorKey: "actualInvested",
        header: "Actual Investment",
        size: 160,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => {
          const val = cell.getValue<number>();
          return val > 0 ? formatINR(val) : dash;
        },
      },
      {
        accessorKey: "currentValue",
        header: "Current Value",
        size: 150,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => {
          const val = cell.getValue<number | null>();
          return val != null ? (
            <span style={{ color: "var(--mantine-color-green-7)" }}>{formatINR(val)}</span>
          ) : (
            dash
          );
        },
      },
      {
        accessorKey: "goalAchievedPct",
        header: "Goal Achieved (%)",
        size: 160,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => <PctCell value={cell.getValue<number | null>()} />,
      },
      {
        accessorKey: "growthPct",
        header: "Growth (%)",
        size: 130,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => <PctCell value={cell.getValue<number | null>()} />,
      },
      {
        accessorKey: "schemeName",
        header: "Scheme Name",
        size: 220,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
      {
        accessorKey: "investmentType",
        header: "Investment Type",
        size: 140,
        enableColumnFilter: false,
        Cell: ({ cell }) => enumLabel(INVESTMENT_TYPE_LABEL, cell.getValue<string>()) ?? "—",
      },
    ],
    []
  );

  return (
    <Flex direction="column" gap="md">
      <Text fw={500} fz="md">
        All goals
      </Text>

      <MantineReactTable
        columns={columns}
        data={rows}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        enableGlobalFilter={false}
        positionToolbarAlertBanner="none"
        initialState={{
          columnPinning: {},
          density: "xs",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 400,
            fontSize: "var(--mantine-font-size-sm)",
            color: theme.colors.slate[5],
            borderRight: "1px solid var(--mantine-color-gray-2)",
            backgroundColor: "var(--mantine-color-gray-0)",
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 300px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
      />
    </Flex>
  );
}
